'use client';

import { useCallback } from 'react';
import { useOptionalBookingModal } from '../modals/BookingModalContext';
import { landingPageEvents } from '../PostHogProvider';

/**
 * The one booking action every marketing CTA shares: record the click as
 * `ctaClicked(ctaId, location)`, then open the booking modal on the "Talk to
 * Nathan" form, with `location` as the open's placement. The control
 * itself is the caller's (`Button`, or a page's own skin), so the visual
 * primitives never import the modal or the analytics module; this hook is
 * the only place the two meet.
 *
 * The homepage's `talk-to-us` clicks ride on it through `useTalkToUs`.
 * Reputation's `ReputationCta` calls it directly, recording
 * `review-my-profiles` at `reputation-<location>` under the page's own skin.
 *
 * Safe without the provider (the click is still recorded, nothing opens), so
 * a section renders in isolation without wiring a modal it does not own. An
 * `override` replaces the whole action: the caller's handler runs and neither
 * the event nor the modal fires.
 */
export function useBookingCta(ctaId: string, location: string, override?: () => void): () => void {
  const modal = useOptionalBookingModal();
  return useCallback(() => {
    if (override) {
      override();
      return;
    }
    landingPageEvents.ctaClicked(ctaId, location);
    modal?.openModal({ placement: location });
  }, [ctaId, location, modal, override]);
}
